import React, { useState } from "react";
import { X, Mail, KeyRound, Lock, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { postWithoutTokenAPI } from "../util/Api";
import toast from "react-hot-toast";

export function ForgotPasswordModal({ isOpen, onClose, t }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error(t("Email is required"));

    setLoading(true);
    try {
      const response = await postWithoutTokenAPI("/auth/forgot-password", { email: email.trim().toLowerCase() });
      if (response?.statusCode === 200) {
        toast.success(t("OTP sent to your email"));
        setStep(2);
      } else {
        toast.error(response?.message || t("Failed to send OTP"));
      }
    } catch (err) {
      toast.error(t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return toast.error(t("OTP is required"));
    if (newPassword.length < 8) return toast.error(t("Password must be at least 8 characters long"));

    setLoading(true);
    try {
      const response = await postWithoutTokenAPI("/auth/reset-password", { 
        email: email.trim().toLowerCase(), 
        otp: otp.trim(),
        new_password: newPassword 
      });

      if (response?.statusCode === 200) {
        toast.success(t("Password successfully reset"));
        onClose();
        setStep(1);
        setEmail("");
        setOtp("");
        setNewPassword("");
      } else {
        toast.error(response?.message || t("Failed to reset password"));
      }
    } catch (err) {
      toast.error(t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl relative">
        <button 
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 md:p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("Forgot Password")}
            </h2>
            <p className="text-sm text-gray-500">
              {step === 1 
                ? t("Enter your registered email address to receive a reset OTP.") 
                : t("Enter the OTP sent to your email and your new password.")}
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="form-group">
                <Label htmlFor="forgot-email" className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                  <Mail className="w-3.5 h-3.5 text-gray-500" />
                  {t("Email Address")}
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="your-email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-lg h-11 text-sm"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                style={{ backgroundColor: '#dc2626' }}
                className="w-full h-11 rounded-lg text-white font-semibold shadow-md mt-4"
              >
                {loading ? t("Please wait...") : t("Send Reset OTP")}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="form-group">
                <Label htmlFor="forgot-otp" className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                  <KeyRound className="w-3.5 h-3.5 text-gray-500" />
                  {t("Enter OTP")}
                </Label>
                <Input
                  id="forgot-otp"
                  type="text"
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-lg h-11 text-sm text-center tracking-widest font-mono"
                  required
                  maxLength={6}
                />
              </div>
              
              <div className="form-group">
                <Label htmlFor="new-password" className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                  <Lock className="w-3.5 h-3.5 text-gray-500" />
                  {t("New Password")}
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="••••••••••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-gray-50 border-gray-200 rounded-lg h-11 text-sm"
                  required
                />
                <div className="mt-2 flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${newPassword.length >= 8 ? "text-green-500" : "text-gray-300"}`} />
                  <span className={`text-xs ${newPassword.length >= 8 ? "text-gray-700 font-medium" : "text-gray-500"}`}>
                    {t("Minimum 8 characters")}
                  </span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                style={{ backgroundColor: '#dc2626' }}
                className="w-full h-11 rounded-lg text-white font-semibold shadow-md mt-4"
              >
                {loading ? t("Please wait...") : t("Reset Password")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
